import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { t } from '../../../../utils/textUtils';

export default function CategoryDialog({ 
  open, 
  onClose, 
  onSave, 
  loading = false, 
  categoryData = null, // If null = add mode, if has data = edit mode
  mode = 'add' // 'add' or 'edit'
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    displayOrder: ''
  });

  const [errors, setErrors] = useState({});

  const isEditMode = mode === 'edit' || categoryData !== null;

  // Pre-fill form data when dialog opens with category data (edit mode)
  useEffect(() => {
    if (open) {
      if (isEditMode && categoryData) {
        setFormData({
          name: categoryData.name || '',
          description: categoryData.description || '',
          displayOrder: categoryData.displayOrder?.toString() || ''
        });
      } else {
        // Reset form for add mode
        setFormData({
          name: '',
          description: '',
          displayOrder: ''
        });
      }
      setErrors({});
    }
  }, [open, categoryData, isEditMode]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nama kategori pekerjaan wajib diisi';
    }
    
    if (formData.displayOrder && isNaN(Number(formData.displayOrder))) {
      newErrors.displayOrder = 'Urutan tampilan harus berupa angka';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      displayOrder: formData.displayOrder.trim() ? Number(formData.displayOrder.trim()) : 0
    };

    onSave(payload);
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        description: '',
        displayOrder: ''
      });
      setErrors({});
      onClose();
    }
  };

  const dialogTitle = isEditMode ? 'EDIT KATEGORI PEKERJAAN' : 'TAMBAH KATEGORI PEKERJAAN BARU';

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div">
          {dialogTitle}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
              NAMA KATEGORI PEKERJAAN *
            </Typography>
            <TextField
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              disabled={loading}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f8f9fa',
                }
              }}
            />
          </Box>
          
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
              DESKRIPSI
            </Typography>
            <TextField
              value={formData.description}
              onChange={handleChange('description')}
              fullWidth
              multiline
              rows={4}
              disabled={loading}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f8f9fa',
                }
              }}
            />
          </Box>
          
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
              URUTAN TAMPILAN
            </Typography>
            <TextField
              value={formData.displayOrder}
              onChange={handleChange('displayOrder')}
              error={!!errors.displayOrder}
              helperText={errors.displayOrder}
              fullWidth
              disabled={loading}
              variant="outlined"
              type="number"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f8f9fa',
                }
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{ minWidth: 80 }}
        >
          BATAL
        </Button>
        <Button 
          onClick={handleSave}
          disabled={loading || !formData.name.trim()}
          variant="contained"
          sx={{ minWidth: 80 }}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          SIMPAN
        </Button>
      </DialogActions>
    </Dialog>
  );
}
